import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, TimerReset, CheckCircle2, AlertTriangle, Users2, Pencil } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const STATUS_TONE: Record<string, string> = {
  present: "good", approved: "good", incomplete: "warn", late: "warn",
  unscheduled: "info", absent: "critical", no_show: "critical",
};
const fmtH = (m: number | null) => (m == null ? "—" : `${(m / 60).toFixed(1)}h`);
const fmtVar = (m: number | null) => (m == null ? "—" : `${m > 0 ? "+" : ""}${(m / 60).toFixed(1)}h`);

export default function TimeAttendance() {
  const {
    t
  } = useI18n();

  const { modules } = useAuth();
  const [stores, setStores] = useState<any[]>([]);
  const [storeId, setStoreId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [tab, setTab] = useState("timecards");

  useEffect(() => {
    api.get("/org/stores").then((r) => { setStores(r.data.stores); if (r.data.stores[0]) setStoreId(r.data.stores[0].id); }).catch(() => {});
  }, []);

  if (modules.time_attendance === false) {
    return (
      <div>
        <PageHeader title="Time & Attendance" testId="ta-header" />
        <EmptyState icon={<TimerReset size={28} />} title="Time & Attendance module is inactive"
          description="Enable it in Admin → Modules to review timecards and attendance." />
      </div>
    );
  }

  return (
    (<div>
      <PageHeader title="Time & Attendance" subtitle="Timecards, exceptions, approvals and daily attendance" testId="ta-header" />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">{t("Store")}</Label>
          <Select value={storeId} onValueChange={setStoreId}>
            <SelectTrigger className="mt-1 w-64" data-testid="ta-store-select"><SelectValue placeholder={t("Select store")} /></SelectTrigger>
            <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{t("Store")}{" "}{s.store_number}· {s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">{t("Business date")}</Label><Input type="date" className="mt-1 w-44" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ta-date-input" /></div>
      </div>
      {!storeId ? <LoadingBlock /> : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="timecards" data-testid="ta-tab-timecards">{t("Timecards")}</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="ta-tab-attendance">{t("Attendance")}</TabsTrigger>
            <TabsTrigger value="corrections" data-testid="ta-tab-corrections">{t("Corrections")}</TabsTrigger>
            <TabsTrigger value="export" data-testid="ta-tab-export">{t("Export")}</TabsTrigger>
            <TabsTrigger value="config" data-testid="ta-tab-config">{t("Settings")}</TabsTrigger>
          </TabsList>
          <TabsContent value="timecards"><Timecards storeId={storeId} date={date} /></TabsContent>
          <TabsContent value="attendance"><Attendance storeId={storeId} date={date} /></TabsContent>
          <TabsContent value="corrections"><Corrections storeId={storeId} /></TabsContent>
          <TabsContent value="export"><ExportPanel storeId={storeId} date={date} /></TabsContent>
          <TabsContent value="config"><ConfigPanel /></TabsContent>
        </Tabs>
      )}
    </div>)
  );
}

const Timecards: React.FC<{ storeId: string; date: string }> = ({ storeId, date }) => {
  const {
    t: tI18n
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [onlyExc, setOnlyExc] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/time/timecards?store_id=${storeId}&business_date=${date}&only_exceptions=${onlyExc}`)
      .then((r) => setRows(r.data.timecards)).finally(() => setLoading(false));
  }, [storeId, date, onlyExc]);
  useEffect(() => { load(); }, [load]);
  if (loading) return <LoadingBlock />;
  return (
    (<div>
      <label className="mb-3 flex items-center gap-2 text-sm" data-testid="ta-only-exceptions">
        <input type="checkbox" checked={onlyExc} onChange={(e) => setOnlyExc(e.target.checked)} />{" "}{tI18n("Only timecards with open exceptions")}</label>
      {rows.length === 0 ? <EmptyState icon={<Clock size={26} />} title="No timecards" description="No punches recorded for this store and date." /> : (
        <div className="space-y-2" data-testid="ta-timecards-list">
          {rows.map((t) => (
            <Card key={t.id} className="flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`ta-timecard-${t.id}`}>
              <div className="min-w-[180px]">
                <div className="font-display font-semibold">{t.profile_name}</div>
                <div className="text-xs text-muted-foreground">{t.clock_in || "—"}→ {t.clock_out || "—"}· {fmtH(t.worked_minutes)}{" "}{tI18n("worked")}</div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground">{tI18n("var")}{" "}{fmtVar(t.variance_minutes)}</span>
                {t.unresolved_exceptions.length > 0 && <span className="flex items-center gap-1 text-status-warn" data-testid={`ta-exc-${t.id}`}><AlertTriangle size={13} />{t.unresolved_exceptions.length}</span>}
                <StatusPill status={t.approval.status === "approved" ? "good" : STATUS_TONE[t.status] || "info"}>{t.approval.status === "approved" ? tI18n("approved") : t.status}</StatusPill>
                <Button size="sm" variant="outline" onClick={() => setDetailId(t.id)} data-testid={`ta-review-${t.id}`}>{tI18n("Review")}</Button>
              </div>
            </Card>
          ))}
        </div>
      )}
      {detailId && <TimecardDialog tcId={detailId} onClose={() => setDetailId(null)} onDone={load} />}
    </div>)
  );
};

const TimecardDialog: React.FC<{ tcId: string; onClose: () => void; onDone: () => void }> = ({ tcId, onClose, onDone }) => {
  const {
    t
  } = useI18n();

  const [tc, setTc] = useState<any>(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [ci, setCi] = useState("");const [co, setCo] = useState("");const [bk, setBk] = useState("");const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const load = useCallback(() => { api.get(`/time/timecards/${tcId}`).then((r) => setTc(r.data.timecard)).catch(() => {}); }, [tcId]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onDone(); };
  const adjust = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    try {
      await api.post(`/time/timecards/${tcId}/adjust`, { clock_in: ci || null, clock_out: co || null, break_minutes: bk === "" ? null : Number(bk), reason });
      toast.success("Correction recorded"); setAdjOpen(false); setCi(""); setCo(""); setBk(""); setReason(""); refresh();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resolve = async (code: string) => {
    const n = window.prompt(tl("Resolution note for “{code}”", { code }));
    if (!n) return;
    try { await api.post(`/time/timecards/${tcId}/resolve`, { code, note: n }); toast.success("Exception resolved"); refresh(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approve = async () => {
    try { await api.post(`/time/timecards/${tcId}/approve`, { note: note || null }); toast.success("Timecard approved"); refresh(); onClose(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!tc) return null;
  const approved = tc.state === "approved" || tc.state === "exported";
  const reopen = async () => {
    const r = window.prompt("Reason for reopening this timecard");
    if (!r) return;
    try { await api.post(`/time/timecards/${tcId}/reopen`, { reason: r }); toast.success("Timecard reopened"); refresh(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    (<Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="ta-timecard-dialog">
        <DialogHeader><DialogTitle>{tc.profile_name} · {tc.business_date}</DialogTitle>
          <DialogDescription>{t(
            "Punches, corrections and approval. Approved timecards are locked and feed reconciliation."
          )}</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md border border-border p-2"><div className="font-bold">{tc.clock_in || "—"}</div><div className="text-xs text-muted-foreground">{t("In")}</div></div>
            <div className="rounded-md border border-border p-2"><div className="font-bold">{tc.clock_out || "—"}</div><div className="text-xs text-muted-foreground">{t("Out")}</div></div>
            <div className="rounded-md border border-border p-2"><div className="font-bold">{fmtH(tc.worked_minutes)}</div><div className="text-xs text-muted-foreground">{t("Worked (var")}{" "}{fmtVar(tc.variance_minutes)})</div></div>
          </div>
          {tc.shift && <div className="text-xs text-muted-foreground">{t("Scheduled:")}{" "}{tc.shift.start}–{tc.shift.end}({tc.shift.role || "any"})</div>}
          <div>
            <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{t("Punches")}</div>
            {(tc.punches || []).length === 0 ? <div className="text-xs text-muted-foreground">{t("No punches")}</div> : (
              <div className="flex flex-wrap gap-1">{tc.punches.map((p: any) => <span key={p.id} className="rounded bg-secondary px-2 py-0.5 text-[11px]">{p.type.replace("_", " ")} {String(Math.floor(p.local_minute / 60)).padStart(2, "0")}:{String(p.local_minute % 60).padStart(2, "0")}</span>)}</div>
            )}
          </div>
          {tc.exceptions.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{t("Exceptions")}</div>
              <div className="space-y-1">
                {tc.exceptions.map((e: any) => (
                  <div key={e.code} className="flex items-center justify-between rounded-md border border-border p-2" data-testid={`ta-exception-${e.code}`}>
                    <span className={e.resolved ? t("text-muted-foreground line-through") : t("text-status-warn")}>{e.code.replace("_", " ")} — {e.detail}</span>
                    {!e.resolved && !approved && <Button size="sm" variant="ghost" onClick={() => resolve(e.code)} data-testid={`ta-resolve-${e.code}`}>{t("Resolve")}</Button>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {(tc.adjustments || []).length > 0 && (
            <div className="text-xs text-muted-foreground">{tc.adjustments.length}{" "}{t("correction(s) on record")}</div>
          )}
          {!approved && (
            <div className="rounded-md border border-border p-2">
              {!adjOpen ? <Button size="sm" variant="outline" onClick={() => setAdjOpen(true)} data-testid="ta-adjust-open"><Pencil size={13} className="mr-1" />{t("Correct times")}</Button> : (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div><Label className="text-xs">{t("Clock in")}</Label><Input type="time" value={ci} onChange={(e) => setCi(e.target.value)} data-testid="ta-adj-in" /></div>
                    <div><Label className="text-xs">{t("Clock out")}</Label><Input type="time" value={co} onChange={(e) => setCo(e.target.value)} data-testid="ta-adj-out" /></div>
                    <div><Label className="text-xs">{t("Break min")}</Label><Input type="number" value={bk} onChange={(e) => setBk(e.target.value)} data-testid="ta-adj-break" /></div>
                  </div>
                  <Input placeholder={t("Reason (required)")} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ta-adj-reason" />
                  <Button size="sm" onClick={adjust} data-testid="ta-adj-save">{t("Save correction")}</Button>
                </div>
              )}
            </div>
          )}
        </div>
        {approved ? (
          <div className="flex flex-wrap items-center gap-3 text-sm text-status-good" data-testid="ta-approved-badge">
            <span className="flex items-center gap-1"><CheckCircle2 size={15} />{tc.state === "exported" ? t("Exported") : t("Approved")}{tc.approval.note ? ` · ${tc.approval.note}` : ""}</span>
            {tc.reconciliation_status && <span className="text-status-warn">{tc.reconciliation_status.replace("_", " ")}</span>}
            <Button size="sm" variant="outline" onClick={reopen} data-testid="ta-reopen-btn">{t("Reopen")}</Button>
          </div>
        ) : (
          <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch">
            <Input placeholder={t("Approval note (required if exceptions unresolved)")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="ta-approve-note" />
            <Button onClick={approve} data-testid="ta-approve-btn"><CheckCircle2 size={15} className="mr-1" />{t("Approve timecard")}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>)
  );
};

const Attendance: React.FC<{ storeId: string; date: string }> = ({ storeId, date }) => {
  const {
    t
  } = useI18n();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/time/attendance?store_id=${storeId}&business_date=${date}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [storeId, date]);
  if (loading) return <LoadingBlock />;
  const s = data?.summary || {};
  const cards = [["Scheduled", s.scheduled], ["Present", s.present], ["Absent", s.absent], ["Late", s.late], ["On clock", s.on_clock]];
  return (
    (<div data-testid="ta-attendance">
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
        {cards.map(([k, v]) => (
          <Card key={k as string} className="p-3 text-center"><div className="font-display text-2xl font-bold">{v ?? 0}</div><div className="text-xs text-muted-foreground">{k as string}</div></Card>
        ))}
      </div>
      {(data?.rows || []).length === 0 ? <EmptyState icon={<Users2 size={26} />} title="No scheduled staff or punches" description="Nothing to show for this date." /> : (
        <div className="space-y-2">
          {data.rows.map((r: any) => (
            <Card key={r.profile_id} className="flex items-center justify-between p-3" data-testid={`ta-att-${r.profile_id}`}>
              <div><div className="font-medium">{r.profile_name}</div><div className="text-xs text-muted-foreground">{r.scheduled || "unscheduled"} · {fmtH(r.worked_minutes)}</div></div>
              <div className="flex items-center gap-2">
                {(r.state === "IN" || r.state === "BREAK") && <span className="h-2 w-2 rounded-full bg-status-good" title={t("on the clock")} />}
                <StatusPill status={STATUS_TONE[r.status] || "info"}>{stat(r.status)}</StatusPill>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>)
  );
};

const Corrections: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/time/corrections?store_id=${storeId}&status=pending`).then((r) => setRows(r.data.corrections)).finally(() => setLoading(false));
  }, [storeId]);
  useEffect(() => { load(); }, [load]);
  const decide = async (id: string, approve: boolean) => {
    const note = approve ? undefined : window.prompt("Reason for denial (required)") || undefined;
    if (!approve && !note) return;
    try { await api.post(`/time/corrections/${id}/decision`, { approve, note }); toast.success(approve ? tl("Applied") : tl("Denied")); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (loading) return <LoadingBlock />;
  if (rows.length === 0) return <EmptyState icon={<Pencil size={26} />} title="No pending correction requests" description="Employee time correction requests appear here." />;
  return (
    (<div className="space-y-2" data-testid="ta-corrections-list">
      {rows.map((c) => (
        <Card key={c.id} className="flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`ta-correction-${c.id}`}>
          <div className="text-sm">
            <div className="font-medium">{c.profile_name} · {c.type.replace("_", " ")}</div>
            <div className="text-xs text-muted-foreground">{t("proposes")}{" "}{c.proposed.clock_in || "—"}→{c.proposed.clock_out || "—"}{c.proposed.break_minutes != null ? ` · ${tl("break")} ${c.proposed.break_minutes}m` : ""}— {c.reason}</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => decide(c.id, true)} data-testid={`ta-correction-approve-${c.id}`}><CheckCircle2 size={14} className="mr-1" />{t("Apply")}</Button>
            <Button size="sm" variant="outline" onClick={() => decide(c.id, false)} data-testid={`ta-correction-deny-${c.id}`}>{t("Deny")}</Button>
          </div>
        </Card>
      ))}
    </div>)
  );
};

const ExportPanel: React.FC<{ storeId: string; date: string }> = ({ storeId, date }) => {
  const {
    t
  } = useI18n();

  const [preview, setPreview] = useState<any>(null);
  const [exports, setExports] = useState<any[]>([]);
  const load = useCallback(() => {
    api.get(`/time/export/preview?store_id=${storeId}&business_date=${date}`).then((r) => setPreview(r.data)).catch(() => {});
    api.get(`/time/exports?store_id=${storeId}`).then((r) => setExports(r.data.exports)).catch(() => {});
  }, [storeId, date]);
  useEffect(() => { load(); }, [load]);
  const runExport = async () => {
    try { const r = await api.post("/time/export", { store_id: storeId, business_date: date, idempotency_key: `${storeId}-${date}-${Date.now()}` }); toast.success(tl("Exported {rows} rows · checksum {checksum}", { rows: r.data.row_count, checksum: r.data.checksum })); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    (<div data-testid="ta-export">
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="font-display text-sm font-semibold">{t("Approved-time export preview (")}{preview?.count ?? 0}{" "}{t("rows · mapping")}{" "}{preview?.mapping_version || "v1"})</div>
          <Button size="sm" onClick={runExport} disabled={!preview?.count} data-testid="ta-export-run">{t("Export approved time")}</Button>
        </div>
        {preview?.rows?.length ? (
          <div className="mt-3 overflow-x-auto text-xs">
            <table className="w-full">
              <thead><tr className="text-left text-muted-foreground"><th className="p-1">{t("Emp")}</th><th className="p-1">{t("Date")}</th><th className="p-1">{t("In")}</th><th className="p-1">{t("Out")}</th><th className="p-1">{t("Hrs")}</th><th className="p-1">{t("State")}</th></tr></thead>
              <tbody>{preview.rows.map((r: any, i: number) => <tr key={i} className="border-t border-border"><td className="p-1">{r.external_employee_id}</td><td className="p-1">{r.date}</td><td className="p-1">{r.clock_in}</td><td className="p-1">{r.clock_out}</td><td className="p-1">{r.worked_hours}</td><td className="p-1">{r.exception_state}</td></tr>)}</tbody>
            </table>
          </div>
        ) : <div className="mt-2 text-sm text-muted-foreground">{t("No approved timecards for this date. Approve timecards first.")}</div>}
      </Card>
      {exports.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("Export batches")}</div>
          <div className="space-y-2" data-testid="ta-export-batches">
            {exports.map((e) => (
              <Card key={e.id} className="flex items-center justify-between p-3 text-sm">
                <span>{e.business_date}· {e.row_count}{" "}{t("rows ·")}{" "}{e.checksum}</span>
                <StatusPill status={e.reconciliation_status === "reconciled" ? "good" : "warn"}>{e.reconciliation_status.replace("_", " ")}</StatusPill>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>)
  );
};

const ConfigPanel: React.FC = () => {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const [cfg, setCfg] = useState<any>(null);
  const canEdit = hasPerm("modules.manage") || hasPerm("tenant.admin");
  useEffect(() => { api.get("/time/config").then((r) => setCfg(r.data.config)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/time/config", { config: cfg }); toast.success("Settings saved"); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Not authorized"); }
  };
  if (!cfg) return <LoadingBlock />;
  const setG = (k: string, v: any) => setCfg((c: any) => ({ ...c, grace: { ...c.grace, [k]: Number(v) } }));
  const setT = (k: string, v: any) => setCfg((c: any) => ({ ...c, thresholds: { ...c.thresholds, [k]: Number(v) } }));
  return (
    (<Card className="max-w-2xl p-4" data-testid="ta-config">
      <div className="mb-3 font-display text-sm font-semibold">{t("Time & Attendance settings")}</div>
      <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <div><Label className="text-xs">{t("Late grace (min)")}</Label><Input type="number" className="mt-1" value={cfg.grace.late_min} onChange={(e) => setG("late_min", e.target.value)} disabled={!canEdit} data-testid="ta-cfg-late" /></div>
        <div><Label className="text-xs">{t("Early grace (min)")}</Label><Input type="number" className="mt-1" value={cfg.grace.early_min} onChange={(e) => setG("early_min", e.target.value)} disabled={!canEdit} /></div>
        <div><Label className="text-xs">{t("Overtime/day (min)")}</Label><Input type="number" className="mt-1" value={cfg.thresholds.overtime_daily_min} onChange={(e) => setT("overtime_daily_min", e.target.value)} disabled={!canEdit} data-testid="ta-cfg-ot" /></div>
        <div><Label className="text-xs">{t("Long break (min)")}</Label><Input type="number" className="mt-1" value={cfg.thresholds.long_break_min} onChange={(e) => setT("long_break_min", e.target.value)} disabled={!canEdit} /></div>
        <div>
          <Label className="text-xs">{t("Capture mode")}</Label>
          <Select value={cfg.capture.mode} onValueChange={(v) => setCfg((c: any) => ({ ...c, capture: { ...c.capture, mode: v } }))} disabled={!canEdit}>
            <SelectTrigger className="mt-1" data-testid="ta-cfg-capture"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="web">{t("Web / secure flow")}</SelectItem>
              <SelectItem value="device">{t("Store device")}</SelectItem>
              <SelectItem value="import">{t("Import only (external clock)")}</SelectItem>
              <SelectItem value="schedule_compare">{t("Schedule comparison")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t("Rounding")}</Label>
          <label className="mt-2 flex items-center gap-2"><input type="checkbox" checked={cfg.rounding.enabled} onChange={(e) => setCfg((c: any) => ({ ...c, rounding: { ...c.rounding, enabled: e.target.checked } }))} disabled={!canEdit} data-testid="ta-cfg-rounding" /> {cfg.rounding.increment_min}{t("m (")}{cfg.rounding.direction})</label>
        </div>
      </div>
      <div className="mt-3 text-[11px] text-muted-foreground">{t(
        "No biometrics, facial recognition, or covert location tracking. Exceptions are operational warnings, not legal/disciplinary conclusions."
      )}</div>
      {canEdit ? <Button className="mt-3" onClick={save} data-testid="ta-config-save">{t("Save settings")}</Button> : <div className="mt-3 text-xs text-muted-foreground">{t("Read-only — tenant administrator can change settings.")}</div>}
    </Card>)
  );
};
